/**
 * requestTimeout.ts — предел ожидания у запросов кабинета к серверу (WEB-2590).
 *
 * ЧТО ЛОМАЛОСЬ. У общего клиента (`api/client.ts`) предела не было вообще.
 * После «Оставить мои правки» редактор ждёт отметку версии черновика; если
 * этот запрос повисал, автосохранение молчало, пока он не кончится, — то есть
 * бесконечно. Повисшее перечитывание черновика так же держало каркас загрузки.
 *
 * ЧТО ТЕПЕРЬ. У каждого запроса клиента есть общий предел
 * (`DEFAULT_REQUEST_TIMEOUT_MS`). Повисший запрос отказывает сам, отказ
 * узнаётся (`isRequestTimeoutError`) и доходит до человека фразой из словаря
 * на его языке (функция фразы — в отдельном файле, WEB-2696: реестр
 * пределов не должен тянуть словарь в публичную воронку). Отказ без статуса автосохранение
 * считает временным и повторяет по своим прежним правилам (`useAutosave`:
 * отказ без классификации = «retry» с растущей паузой).
 *
 * ДОЛГИЕ ПО ПРИРОДЕ запросы общим пределом не обрываются: у каждого своя
 * строка в реестре `LONG_REQUESTS` (предел, файл, причина), вызов берёт предел
 * только через `longRequest('<ключ>')`. Сторож двух концов —
 * `api/__tests__/client.timeout.web2590.test.ts`: каждая строка реестра
 * использована ровно в своём файле, каждый вызов с пределом, отличным от
 * общего, стоит в реестре, выгрузки (`responseType: 'blob'`) и загрузки
 * файлов (`FormData`) без строки реестра не проходят.
 *
 * Модуль без `import.meta` — поэтому живёт отдельно от `client.ts` (тот в jest
 * не загружается) и проверяется на настоящем axios.
 */

/** Общий предел ожидания запроса кабинета, мс. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Служебная пометка истечения ожидания в тексте ошибки axios. Своя строка,
 * а не разбор «timeout of Nms exceeded»: отличает истечение предела от отмены
 * запроса вызывающим (тот же код ECONNABORTED, текст «Request aborted»).
 */
export const REQUEST_TIMEOUT_MARKER = 'W2A_REQUEST_TIMEOUT';

/** Настройки предела для `axios.create` общего клиента. */
export const CLIENT_TIMEOUT_DEFAULTS = {
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
  timeoutErrorMessage: REQUEST_TIMEOUT_MARKER,
} as const;

export interface LongRequestEntry {
  /** Свой предел, мс — больше общего и конечный. */
  readonly timeoutMs: number;
  /** Файл вызова (от каталога frontend). */
  readonly file: string;
  /** Почему общий предел ему не подходит (служебная строка для разработчика, не для человека). */
  readonly why: string;
  /**
   * Денежное или необратимое действие: сервер доводит его и после того, как
   * кабинет перестал ждать. По истечении человеку — «результат неизвестен,
   * сначала проверьте», а не «попробуйте ещё раз».
   */
  readonly outcomeUnknown?: true;
}

const MINUTE = 60_000;

/** Реестр долгих по природе запросов общего клиента. */
export const LONG_REQUESTS = {
  quizTranslate: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/translations.ts',
    why: 'AI quiz translation runs inside the request: the server answers once the model has translated every screen',
  },
  paywallTranslate: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/translations.ts',
    why: 'AI paywall translation runs inside the request, same as the quiz one',
  },
  regenerateImage: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/aiFunnels.ts',
    why: 'the image is drawn by the model inside the request and billed only when ready',
  },
  editTurnWithFiles: {
    timeoutMs: 3 * MINUTE,
    file: 'src/api/aiFunnels.ts',
    why: 'AI editor turn with attached files: the request body carries the files, longer than the default on a slow network',
  },
  contentImageUpload: {
    timeoutMs: 3 * MINUTE,
    file: 'src/api/uploads.ts',
    why: 'media hub image upload: the request body is the file itself',
  },
  contentFontUpload: {
    timeoutMs: 3 * MINUTE,
    file: 'src/api/uploads.ts',
    why: 'funnel font upload: the request body is the font file itself',
  },
  teamAuditExport: {
    timeoutMs: 3 * MINUTE,
    file: 'src/api/teamAudit.ts',
    why: 'team audit CSV export is built whole for the period',
  },
  trafficLostExport: {
    timeoutMs: 3 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'lost visitors CSV export is built whole for the period',
  },
  trafficLostExportFile: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'finished lost visitors CSV is read from storage after the team journal write; the file is capped at the export row limit',
  },
  chargesExport: {
    timeoutMs: 3 * MINUTE,
    file: 'src/components/project/payments/rules/journalApi.ts',
    why: 'charges journal CSV export is built whole for the period',
  },
  leadsExportFile: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/leadsCabinet.ts',
    why: 'finished leads CSV is read from storage after the team journal write; the file holds every lead of the project (no row cap), larger than the lost-visitors file',
  },
  chargesExportFile: {
    timeoutMs: 2 * MINUTE,
    file: 'src/components/project/payments/rules/journalApi.ts',
    why: 'finished charges journal CSV is read from storage after the team journal write; the file is capped at the export row limit',
  },
  // WEB-3222: миниатюры снимков разбора по ссылке — байты через общий клиент.
  linkParseShot: {
    timeoutMs: 1 * MINUTE,
    file: 'src/api/linkParse.ts',
    why: 'link reading screenshot PNG (up to 2 MB) is read from closed storage',
  },
  teamAuditExportFile: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/teamAudit.ts',
    why: 'finished team activity log CSV is read from storage; the log is kept for 12 months so the file stays small',
  },
  stripeImport: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/stripe.ts',
    why: 'Stripe catalog import: the server pages through Stripe products and prices inside the request',
  },
  stripeCatalogSync: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/stripe.ts',
    why: 'Stripe catalog sync pages through Stripe inside the request',
  },
  storeCatalogImport: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/commerce.ts',
    why: 'store catalog sync pages through the payment provider inside the request',
  },
  // ── Денежные и необратимые: предел больше худшего случая сервера ──
  domainPurchase: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'domain purchase: about 12 external calls in a row, registrar without a limit, 8-10 Cloudflare calls',
    outcomeUnknown: true,
  },
  orgDomainPurchase: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'organization domain purchase: the same chain as the project purchase',
    outcomeUnknown: true,
  },
  domainRenew: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'domain renewal charges and calls the registrar inside the request',
    outcomeUnknown: true,
  },
  domainForceRenew: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'deliberate renewal with a new charge: registrar call inside the request',
    outcomeUnknown: true,
  },
  domainRenewBulk: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'bulk renewal: up to 50 domains renewed one after another inside the request',
    outcomeUnknown: true,
  },
  // WEB-3821: продление домена пула организации — те же пределы, что у проекта.
  orgDomainRenew: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'organization-pool domain renewal: the same charge and registrar call as the project renewal',
    outcomeUnknown: true,
  },
  orgDomainForceRenew: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'organization-pool deliberate renewal with a new charge: the same registrar call as the project retry',
    outcomeUnknown: true,
  },
  orgDomainRenewBulk: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/domains.ts',
    why: 'organization-pool bulk renewal: up to 50 domains renewed one after another inside the request',
    outcomeUnknown: true,
  },
  contextAnalyze: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/context.ts',
    why: 'AI context analysis: server budget 90 s, charged inside the request',
    outcomeUnknown: true,
  },
  stripeConnect: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/stripe.ts',
    why: 'Stripe connect: key checks and webhook setup on the Stripe side inside the request',
    outcomeUnknown: true,
  },
  stripeDisconnect: {
    timeoutMs: 5 * MINUTE,
    file: 'src/api/stripe.ts',
    why: 'Stripe disconnect cancels customer subscriptions one by one inside the request',
    outcomeUnknown: true,
  },
  // ── Долгие, но повторяемые ──
  inviteEmails: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/projectInvites.ts',
    why: 'invite emails are sent one by one inside the request',
  },
  statsSummary: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/statistics.ts',
    why: 'statistics report is aggregated over the whole period',
  },
  // WEB-3964: итог A/B-тестов страницы списка — один разрез variant за период.
  abSummaries: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/statistics.ts',
    why: 'A/B test results of a list page are aggregated over the whole period',
  },
  // WEB-3772: пробный период и причины ухода по журналу подписок за период.
  subscriptionOutcomes: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/moneyAnalytics.ts',
    why: 'trial outcomes and churn reasons are read from the subscription journal over the whole period',
  },
  // WEB-3772 юнит 3: «По тарифам» — счета, возвраты и споры журнала за период.
  planBreakdown: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/moneyAnalytics.ts',
    why: 'plan breakdown sums invoices, refunds and lost disputes of the subscription journal over the whole period',
  },
  // WEB-3772 юнит «подписки по дням»: новые и закончившиеся подписки журнала по суткам.
  subscriptionsDaily: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/moneyAnalytics.ts',
    why: 'new and ended subscriptions are counted from the subscription journal over the whole period by day',
  },
  // WEB-3772 юнит 9: доход с платящего по дням жизни — журнал подписок платящих до 365 суток.
  payerLifetime: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/moneyAnalytics.ts',
    why: 'revenue per paying subscription sums invoices, refunds and lost disputes of the subscription journal over up to 365 days',
  },
  // WEB-3772 юнит 9: потерянные оплаты — эпизоды проблемы оплаты журнала подписок за период.
  lostPayments: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/moneyAnalytics.ts',
    why: 'payment problem episodes and their outcomes are read from the subscription journal over the whole period',
  },
  statsDaily: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/statistics.ts',
    why: 'daily statistics report is aggregated over the whole period',
  },
  analyticsFunnel: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/analytics.ts',
    why: 'funnel report is aggregated over the whole period',
  },
  analyticsUtm: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/analytics.ts',
    why: 'UTM report is aggregated over the whole period',
  },
  analyticsCohortMatrix: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/analytics.ts',
    why: 'cohort matrix is aggregated over the whole period',
  },
  analyticsAnswers: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/analytics.ts',
    why: 'answers report is aggregated over the whole period',
  },
  orgStats: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/organizations.ts',
    why: 'organization report is aggregated across all projects',
  },
  orgDaily: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/organizations.ts',
    why: 'organization daily report is aggregated across all projects',
  },
  orgDailyByProject: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/organizations.ts',
    why: 'organization daily report per project is aggregated across all projects',
  },
  // WEB-3772: то же по проектам организации.
  orgSubscriptionOutcomes: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/organizations.ts',
    why: 'organization trial outcomes and churn reasons are read from the subscription journal across all projects',
  },
  // WEB-3741: выручка организации по странам и валютам.
  orgCountryRevenue: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/organizations.ts',
    why: 'organization revenue by country scans the events and the subscription journal across all projects',
  },
  trafficTraceSearch: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'visitor path search by email scans the events of the period',
  },
  // Пять загрузок «Аудита трафика» (воронка, сводка, список оборвавшихся,
  // зацикливания, отвал по экранам) — ручки отчётов сервера: долгий запрос к
  // базе сервер обрывает сам и отвечает советом сузить период, кабинет обязан
  // ждать дольше. Сторож двух концов —
  // `api/__tests__/report-routes-long-requests.rev49.test.ts`.
  trafficFunnel: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'traffic audit funnel is aggregated over the whole period',
  },
  trafficSummary: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'traffic audit install summary is aggregated over the whole period',
  },
  trafficLost: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'lost visitors list is selected and counted over the whole period',
  },
  trafficCycles: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'funnel loop report scans the raw events of the period',
  },
  trafficScreens: {
    timeoutMs: 2 * MINUTE,
    file: 'src/api/trafficAudit.ts',
    why: 'screen drop-off report scans the raw events of the period',
  },
} as const satisfies Record<string, LongRequestEntry>;

export type LongRequestKey = keyof typeof LONG_REQUESTS;

/**
 * Ключ настроек запроса: у запроса «результат неизвестен» по истечении.
 * Axios несёт неизвестные ключи настроек до ошибки (`error.config`).
 */
export const OUTCOME_UNKNOWN_CONFIG_KEY = 'w2aOutcomeUnknown';

export interface LongRequestConfig {
  timeout: number;
  [OUTCOME_UNKNOWN_CONFIG_KEY]?: true;
}

/** Предел долгого запроса — для настроек вызова: `{ ...longRequest('ключ') }`. */
export function longRequest(key: LongRequestKey): LongRequestConfig {
  const entry: LongRequestEntry = LONG_REQUESTS[key];
  return entry.outcomeUnknown
    ? { timeout: entry.timeoutMs, [OUTCOME_UNKNOWN_CONFIG_KEY]: true }
    : { timeout: entry.timeoutMs };
}

/**
 * Запросы общего клиента, которые ОСОЗНАННО без своей строки реестра, хотя
 * по виду похожи на долгие. Сторож требует, чтобы маркер стоял в файле.
 */
export const LONG_REQUEST_EXCEPTIONS = {
  avatarBase64: {
    file: 'src/api/storage.ts',
    marker: 'image_base64',
    why: 'avatar goes as a small base64 data URL in a JSON body, size is capped before sending; the default limit is enough',
  },
} as const;

/**
 * Долгие запросы, которые идут МИМО общего клиента — предел им общий не
 * ставит по построению. Названы здесь, чтобы список долгих был одним;
 * сторож проверяет, что в файле по-прежнему стоит названный способ связи.
 */
// Пометка потока событий собрана из частей: серверный сторож билетов
// (stream-ticket-ends.web2385) считает открывающим поток любой файл кабинета,
// где эта строка стоит слитно, а здесь она — только имя способа связи.
const EVENT_SOURCE_MARKER = ['new', 'EventSource('].join(' ');

export const LONG_REQUESTS_OUTSIDE_CLIENT = {
  funnelGenerationStream: {
    file: 'src/hooks/useFunnelGenerationStream.ts',
    marker: EVENT_SOURCE_MARKER,
    why: 'funnel generation stream lives for minutes; the hook owns drop and reconnect',
  },
  editThreadStream: {
    file: 'src/hooks/useEditThreadStream.ts',
    marker: EVENT_SOURCE_MARKER,
    why: 'AI editor turn stream; the turn result is fetched by polling with its own ceilings',
  },
  styleImagePut: {
    file: 'src/api/aiFunnels.ts',
    marker: 'await fetch(data.uploadUrl',
    why: 'style image goes straight to storage via a signed URL, not to our server',
  },
} as const;

/** Истёк ли у запроса предел ожидания (а не отмена, не обрыв сети, не отказ сервера). */
export function isRequestTimeoutError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { code, message, response } = error as {
    code?: unknown;
    message?: unknown;
    response?: unknown;
  };
  if (response) return false;
  if (code === 'ETIMEDOUT') return true;
  return code === 'ECONNABORTED' && message === REQUEST_TIMEOUT_MARKER;
}
